import { DateHelper, IDateTimeType } from '@connect-soft/date-helper';
import { DateValidationError, DatePicker as MuiDatePicker, DatePickerProps } from '@mui/x-date-pickers';
import { forwardRef, useCallback } from 'react';
import { TextField, ITextFieldProps } from '../TextField';
import DatePickerErrorMessages from './DatePickerErrorMessages';

export type IDatePickerProps = Omit<
    DatePickerProps<IDateTimeType>,
    'defaultValue' | 'label' | 'minDate' | 'maxDate' | 'name' | 'value' | 'onError' | 'onChange' | 'renderInput'
> &
    Pick<
        ITextFieldProps,
        'enableDOMAttributeRequired' | 'error' | 'helperText' | 'hidden' | 'label' | 'name' | 'required' | 'size'
    > & {
        minDate?: DatePickerProps<IDateTimeType>['minDate'] | Date;
        maxDate?: DatePickerProps<IDateTimeType>['maxDate'] | Date;
        value?: DatePickerProps<IDateTimeType>['value'] | Date;
        onError?: (message: string) => void;
        onChange?: (value: IDateTimeType | null) => void;
    };

export const DatePicker = forwardRef<HTMLDivElement, IDatePickerProps>(function InnerDatePicker(
    {
        // inputFormat = config.date.inputFormat,
        disabled,
        enableDOMAttributeRequired,
        error,
        helperText,
        label,
        maxDate,
        minDate,
        name,
        readOnly,
        required,
        size,
        value = null,
        onError,
        onChange,
        ...rest
    },
    ref
) {
    const handleError = useCallback(
        (reason: DateValidationError) => {
            if (!value) {
                return;
            }

            if (onError) {
                onError(DatePickerErrorMessages(reason));
            }
        },
        [value, onError]
    );

    return (
        <MuiDatePicker
            {...rest}
            ref={ref}
            disabled={disabled || readOnly}
            maxDate={DateHelper.from(maxDate).getObj() ?? undefined}
            minDate={DateHelper.from(minDate).getObj() ?? undefined}
            readOnly={readOnly}
            slots={{
                textField: (wrapperProps) => (
                    <TextField
                        data-testid={`datePicker_${name}`}
                        {...wrapperProps}
                        disabled={disabled || readOnly}
                        error={error}
                        enableDOMAttributeRequired={enableDOMAttributeRequired}
                        helperText={helperText}
                        label={label}
                        name={name}
                        readOnly={readOnly}
                        required={required}
                        size={size}
                    />
                )
            }}
            value={DateHelper.from(value).getObj()}
            onError={handleError}
            onChange={
                onChange
                    ? (newValue) => onChange(newValue === null || !newValue.isValid() ? null : newValue)
                    : undefined
            }
        />
    );
});
